import { assetsMap } from '@flobi/artwork-core';
import type { OpenDocument } from '../useEditorDocument';
import type { CanvasHandle } from '../useEditorCanvas';
import type { RasterOptions } from '../rasterExport';

export interface CanvasReferenceSource {
  id: string; name: string; width: number; height: number; artboard: boolean; current?: boolean;
}

/** A whole artboard owns its children; never send them again as separate references. */
export function selectedReferenceSources(scene: OpenDocument['scene'], canvas: Pick<CanvasHandle, 'worldBoxOf'>, selection: readonly string[]): CanvasReferenceSource[] {
  return scene.topmost([...new Set(selection)]).flatMap(id => {
    const node = scene.get(id), bounds = canvas.worldBoxOf(id);
    if (!node || node.visible === false || ['slice', 'effector'].includes(node.type) || !bounds || !(bounds.width > 0) || !(bounds.height > 0)) return [];
    return [{ id, name: node.name || (node.type === 'artboard' ? 'Artboard' : 'Object'), width: bounds.width, height: bounds.height, artboard: node.type === 'artboard' }];
  });
}

export function referenceRasterOptions(source: CanvasReferenceSource, signal: AbortSignal, edge = 2048, format: RasterOptions['format'] = 'png'): RasterOptions {
  if (!(source.width > 0) || !(source.height > 0)) throw Error('This reference has no visible area.');
  return { nodeIds: [source.id], targets: [{ name: source.name, ...(source.artboard ? { artboardId: source.id } : {}) }],
    format, scale: Math.min(2, edge / Math.max(source.width, source.height)), signal, onProgress: () => {} };
}

/** Top-level objects in the current artboard, plus loose canvas objects. */
export function objectReferenceSources(scene: OpenDocument['scene'], canvas: Pick<CanvasHandle, 'worldBoxOf'>, artboardId: string | null): CanvasReferenceSource[] {
  const loose = scene.childrenOf('root').filter(id => scene.get(id)?.type !== 'artboard');
  return selectedReferenceSources(scene, canvas, [...(artboardId ? scene.childrenOf(artboardId) : []), ...loose]);
}

/** Render a snapshot without inserting, hiding or changing any canvas object. */
export async function renderCanvasReference(canvas: Pick<CanvasHandle, 'exportRaster'>, source: CanvasReferenceSource, signal: AbortSignal, format: RasterOptions['format'] = 'png'): Promise<Blob> {
  const bytes = await canvas.exportRaster(referenceRasterOptions(source, signal, 2048, format));
  signal.throwIfAborted();
  return new Blob([bytes as Uint8Array<ArrayBuffer>], { type: format === 'jpg' ? 'image/jpeg' : 'image/png' });
}

/**
 * The saved picture a layer shows at all (an image layer, or a shape filled with a picture), even
 * when it is cropped or has effects: what its reference tile shows at once while the real one is drawn.
 */
export function pictureShownBy(open: Pick<OpenDocument, 'scene' | 'root'>, id: string): { src: string; mediaAssetId?: string } | null {
  const node = open.scene.get(id) as Loose | undefined;
  const assetId = node?.type === 'image' ? node.assetId : node?.fills?.find(paint => paint.visible !== false && paint.type === 'image')?.assetId;
  const asset = assetId ? assetsMap(open.root).get(assetId) as { kind?: string; src?: string; mediaAssetId?: string } | undefined : undefined;
  return asset?.src && asset.kind !== 'video' ? { src: asset.src, mediaAssetId: asset.mediaAssetId } : null;
}

/**
 * How a drawn reference is encoded. An artboard or a picture is a photograph-like, filled
 * rectangle (a picture in an ellipse or star keeps PNG, for its see-through corners): JPEG keeps it a tenth of a PNG's size, so it encodes and uploads in a fraction of
 * the time. Anything else (a cut-out, a shape, text) may need its see-through parts: PNG.
 */
export function referenceFormat(open: Pick<OpenDocument, 'scene' | 'root'>, source: CanvasReferenceSource): RasterOptions['format'] {
  const type = (open.scene.get(source.id) as Loose | undefined)?.type;
  // An artboard goes to the export renderer, which writes only PNG.
  return !source.artboard && (type === 'rect' || type === 'image') && pictureShownBy(open, source.id) ? 'jpg' : 'png';
}

/** A picture already saved in Flobi (a generation, an upload): what a reference can point at as it is. */
export interface SavedPicture { mediaAssetId: string; url: string; mime: string; width: number; height: number; name: string }

type Loose = { type?: string; opacity?: number; isMask?: boolean; visible?: boolean; assetId?: string; clip?: unknown; width?: number; height?: number; name?: string;
  fills?: Loose[]; strokes?: { visible?: boolean }[]; effects?: { visible?: boolean }[]; transform?: unknown; scale?: number; blendMode?: string };

/**
 * The saved picture a layer shows, when showing it is ALL the layer does — an image layer, or a
 * rectangle filled with one picture — with nothing drawn over, around or through it: no stroke,
 * effect, transparency, mask, children or moved crop, and the frame the picture's own shape.
 * Such a layer is used as a reference by that picture's address, never drawn and uploaded again
 * (Tarek, 14 Oct: "why are we still uploading it if it's already generated?"). Anything else is
 * what the canvas shows, so it is drawn.
 */
export function savedPictureOf(open: Pick<OpenDocument, 'scene' | 'root'>, id: string): SavedPicture | null {
  const node = open.scene.get(id) as Loose | undefined;
  if (!node || (node.type !== 'image' && node.type !== 'rect') || node.isMask || (node.opacity ?? 1) < 1 || open.scene.childrenOf(id).length) return null;
  if ((node.strokes ?? []).some(paint => paint.visible !== false) || (node.effects ?? []).some(effect => effect.visible !== false)) return null;
  let assetId: string | undefined;
  if (node.type === 'image') {
    if (node.clip || (node.fills ?? []).some(paint => paint.visible !== false)) return null;
    assetId = node.assetId;
  } else {
    const shown = (node.fills ?? []).filter(paint => paint.visible !== false);
    const paint = shown[0];
    if (shown.length !== 1 || paint?.type !== 'image' || (paint.opacity ?? 1) < 1 || (paint.blendMode ?? 'normal') !== 'normal' || paint.transform || (paint.scale ?? 1) !== 1) return null;
    assetId = paint.assetId;
  }
  const asset = assetId ? assetsMap(open.root).get(assetId) as { kind?: string; mediaAssetId?: string; src?: string; mime?: string; width?: number; height?: number; name?: string } | undefined : undefined;
  if (!asset || asset.kind === 'video' || !asset.mediaAssetId || !asset.src || !(asset.width! > 0) || !(asset.height! > 0)) return null;
  // A frame of another shape shows a crop of the picture, which is not the picture.
  if (node.width! > 0 && node.height! > 0 && Math.abs(node.width! / node.height! - asset.width! / asset.height!) > 0.03 * (asset.width! / asset.height!)) return null;
  return { mediaAssetId: asset.mediaAssetId, url: asset.src, mime: asset.mime || 'image/png', width: asset.width!, height: asset.height!, name: asset.name || node.name || 'Picture' };
}



/**
 * What a paste into the Generate panel brings as references: pictures copied from another app or
 * the desktop, or layers copied on the canvas (⌘C writes them as `{ artwork: [...] }` text). Words
 * bring neither and stay the prompt's.
 */
export function pastedReferences(data: Pick<DataTransfer, 'files' | 'getData'> | null): { pictures: File[]; layerIds: string[] } {
  const pictures = [...(data?.files ?? [])].filter(item => item.type.startsWith('image/') && item.type !== 'image/svg+xml');
  if (pictures.length || !data) return { pictures, layerIds: [] };
  try {
    const read = JSON.parse(data.getData('text/plain')) as { artwork?: unknown };
    const layerIds = Array.isArray(read.artwork) ? read.artwork.flatMap(item => item && typeof item === 'object' && typeof (item as { id?: unknown }).id === 'string' && (item as { node?: unknown }).node ? [(item as { id: string }).id] : []) : [];
    return { pictures, layerIds };
  } catch { return { pictures, layerIds: [] }; }
}
